import { Fragment, useEffect } from 'react';

import { getSessionId } from '@aspendental/shared-utils/utils';

import uiKits from '@/components/DynamicForm/FormElements/ui-kits/ui-kits';
import { dataLayerConfig, IDataLayerEventConfig } from '@/utils/dataLayerConfig';
import { remapObjectWith } from '@/components/DynamicForm/utils/payloadParsing';

import { DynamicFormEl, FormDataType, SystemTaskPayload } from './types/DynamicFormTypes';
import { smplTmplt } from './smplTmplt';
import { validateWith } from './validations';
import { cssprefix } from './utils/utils';

interface InputRowProps {
	field: DynamicFormEl;
	idx: number | string;
	show?: boolean;
	/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
	elLibrary: Record<string, (props: any) => JSX.Element>;
	interactedFields: Record<string, boolean>;
	formData: FormDataType;
	formErrors: Record<string, string[]>;
	completedTasks: Record<string, number>;
	updateCompletedTasks: (tasks: Record<string, number>) => void;
	updateInterActedFields: (fields: Record<string, boolean>) => void;
	handleChange: (
		formElementType: DynamicFormEl['type'],
		formData: FormDataType,
		config: Pick<DynamicFormEl, 'validations' | 'reset' | 'when' | 'customErrs'>
	) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
	skipTo: (index: number) => void;
	midStepCall: (index: number) => Promise<void>;
}

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
type PermittedSystemTaskFn = (...args: any[]) => void;

const systemTasks: Record<string, PermittedSystemTaskFn> = {
	logValue: (key: string, value?: string) => {
		console.log(`${key}: ${typeof value !== 'undefined' ? value : 'absent'}`);
	},
	dataLayerPush: (_, __, payload) => {
		dataLayerConfig({ ...payload, session_id: getSessionId() } as IDataLayerEventConfig);
	},
	dataPush: (_, __, payload: SystemTaskPayload) => {
		const { url, ...payloadRest } = payload;
		if (url?.toString()) {
			fetch(url.toString(), {
				method: 'POST',
				body: JSON.stringify({ ...payloadRest, session_id: getSessionId() }),
			}).catch((err) => console.log(err));
		}
	},
	redirect: (_, __, payload: SystemTaskPayload) => {
		const { url, timeout = 0 } = payload;
		try {
			setTimeout(() => {
				globalThis.location.href = url as string;
			}, timeout as number);
		} catch (err) {
			console.log(err);
		}
	},
	default: () => {
		console.log('unrecognized system task');
	},
};

const InputRow = ({
	field,
	idx,
	show = true,
	elLibrary,
	interactedFields,
	formData,
	formErrors,
	completedTasks,
	updateCompletedTasks,
	updateInterActedFields,
	handleChange,
	skipTo,
	midStepCall,
}: InputRowProps): JSX.Element => {
	const value = field.type !== 'reviewCard' ? formData[field.name] : formData;

	const getElementLibrary = (field: DynamicFormEl) => {
		if (!field.type) {
			return null;
		}

		if (field.override?.uiKit && uiKits[field.override?.uiKit]) {
			const overrideElementLibrary = uiKits[field.override?.uiKit];
			const overrideElementName = overrideElementLibrary[field.type] ? field.type : 'default';
			return overrideElementLibrary[overrideElementName];
		}
		const elementName = elLibrary?.[field.type] ? field.type : 'default';
		return elLibrary[elementName];
	};

	useEffect(() => {
		((show && field?.systemTasks) || []).forEach(({ task, payload }) => {
			const taskIdentifier = `${task}_${field.name}`;
			if (!completedTasks?.[taskIdentifier]) {
				try {
					// modify the payload
					const processedPayload =
						typeof payload === 'undefined' ? formData : remapObjectWith(payload, formData);
					// run the task
					systemTasks[systemTasks?.[task] ? task : 'default'].apply(null, [
						field.name,
						value,
						JSON.parse(smplTmplt(JSON.stringify(processedPayload), formData)),
					]);
					// record that the task has been completed
					updateCompletedTasks({
						...completedTasks,
						[taskIdentifier]: Date.now(),
					});
				} catch (error) {
					console.log(`systemTask error with ${taskIdentifier}: ${(error as Error)?.message}`);
				}
			}
		});
	}, [completedTasks, updateCompletedTasks, field, show, value, formData]);

	const elementToRender = getElementLibrary(field);

	return (
		<Fragment key={`${field.name}_${idx}`}>
			{elementToRender && (
				<div
					className={[`${cssprefix}input-row`]
						.concat(
							show ? '' : ` ${cssprefix}hide`,
							field.span ? ` ${cssprefix}span${field.span}` : '',
							field.labelSpacing && field.type === 'userInteraction'
								? ` ${cssprefix}label-spacer-${field.labelSpacing}`
								: ''
						)
						.join('')}
				>
					{elementToRender({
						...field,
						value,
						onChange: handleChange(field.type, formData, {
							reset: field?.reset,
							when: field?.when,
							validations: field.validations,
							customErrs: field.customErrs,
						}),
						showError: formErrors?.[field.name]?.length && interactedFields[field.name],
						errMsg:
							formErrors[field.name] && interactedFields[field.name] && formErrors[field.name].join('; '),
						onBlur: () => updateInterActedFields({ ...interactedFields, [field.name]: true }),
						skipTo,
						midStepCall,
					})}
				</div>
			)}
			{field.when &&
				field.when?.length > 0 &&
				field.when.map(([whenCond, whenFields], parentIdx) =>
					whenFields.map((whenField, whenIdx) => (
						<InputRow
							key={`${whenField.name}_${parentIdx}_${whenIdx}`}
							field={whenField}
							idx={`${parentIdx}_${whenIdx}`}
							show={validateWith(whenCond, formData[field.name]).res}
							elLibrary={elLibrary}
							interactedFields={interactedFields}
							formData={formData}
							formErrors={formErrors}
							completedTasks={completedTasks}
							updateCompletedTasks={updateCompletedTasks}
							updateInterActedFields={updateInterActedFields}
							handleChange={handleChange}
							skipTo={skipTo}
							midStepCall={midStepCall}
						/>
					))
				)}
		</Fragment>
	);
};

export default InputRow;
